'use client'

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { DEFAULT_SITE_ID } from '@/app/api/base'

interface FlowDebuggerProps {
  sessionId: string | null
  isFlowActive: boolean
  flowSession: any
  hasDefaultFlow: boolean | null
  flowError: string | null
  initializeFlow?: () => void
  isHandedOver?: boolean
  assignedBotId?: number | null
  activeFlowBotId?: number | null
}

export function FlowDebugger({
  sessionId,
  isFlowActive,
  flowSession,
  hasDefaultFlow,
  flowError,
  initializeFlow,
  isHandedOver = false,
  assignedBotId = null,
  activeFlowBotId = null,
}: FlowDebuggerProps) {
  const resolvedSiteId = DEFAULT_SITE_ID
  const siteApiBase = useMemo(() => '/api/be', [])
  const [initTestResult, setInitTestResult] = useState<any>(null)
  const [isVisible, setIsVisible] = useState(false)

  type ActiveFlowTestResult =
    | Awaited<ReturnType<typeof api.flows.getActiveFlow>>
    | { error: string }

  const { data: activeFlowResult, refetch: refetchActiveFlow } =
    useQuery<ActiveFlowTestResult>({
      queryKey: ['flows', 'active', resolvedSiteId] as const,
      queryFn: async () => {
        try {
          return await api.flows.getActiveFlow()
        } catch (error) {
          return {
            error:
              error instanceof Error
                ? error.message
                : 'Unknown active flow error',
          }
        }
      },
      staleTime: 60_000,
    })

  const apiTestResult = useMemo(
    () => activeFlowResult ?? null,
    [activeFlowResult]
  )

  const testFlowAPI = async () => {
    const result = await refetchActiveFlow()
    console.log('🧪 [FLOW DEBUGGER] API refetch result:', result.data)
  }

  const testFlowInit = async () => {
    if (!sessionId) {
      setInitTestResult({ error: 'No session ID available' })
      return
    }

    try {
      console.log('🧪 [FLOW DEBUGGER] Testing flow initialization...')
      const result = await api.flows.initialize({
        sessionId,
        triggerContext: 'chat_initialize',
      })
      console.log('🧪 [FLOW DEBUGGER] Init result:', result)
      setInitTestResult(result)
    } catch (error) {
      console.error('🧪 [FLOW DEBUGGER] Init error:', error)
      setInitTestResult({
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  const testFlowNodes = async () => {
    try {
      console.log('🧪 [FLOW DEBUGGER] Testing flow nodes...')
      // Get the active flow first
      const flowResult =
        (await refetchActiveFlow()).data ?? activeFlowResult ?? null
      if (flowResult?.success && flowResult?.data) {
        const flowId = flowResult.data.id
        console.log('🧪 [FLOW DEBUGGER] Flow ID:', flowId)

        // Use the debug session endpoint to get flow details
        const debugResponse = await fetch(
          `${siteApiBase}/chat/flows/debug-session/test-session`
        )
        const debugData = await debugResponse.json()
        console.log(
          '🧪 [FLOW DEBUGGER] Debug status:',
          debugData?.success !== false ? 'success' : 'failed'
        )
        if (debugData?.success === false || debugData?.error) {
          console.error('❌ [FLOW DEBUGGER] Debug error:', {
            success: debugData?.success,
            error: debugData?.error,
            message: debugData?.message,
          })
        }

        setInitTestResult({
          flowId,
          debugData,
          message: 'Check console for detailed flow structure',
        })
      } else {
        setInitTestResult({ error: 'No active flow found' })
      }
    } catch (error) {
      console.error('🧪 [FLOW DEBUGGER] Nodes test error:', error)
      setInitTestResult({
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null
  }

  if (!isVisible) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsVisible(true)}
          className="bg-purple-600 text-white px-3 py-1 rounded text-xs hover:bg-purple-700"
        >
          🔍 Flow Debug
        </button>
      </div>
    )
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-white border border-gray-300 rounded-lg shadow-lg p-4 max-w-md text-xs">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-bold text-purple-600">Flow Debugger</h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      <div className="space-y-2">
        <div>
          <strong>Site ID:</strong> {resolvedSiteId}
        </div>
        <div>
          <strong>Session ID:</strong>{' '}
          <span className={sessionId ? 'text-green-600' : 'text-red-600'}>
            {sessionId ? sessionId.slice(-8) : 'None'}
          </span>
        </div>
        <div>
          <strong>Is Flow Active:</strong>{' '}
          <span className={isFlowActive ? 'text-green-600' : 'text-red-600'}>
            {isFlowActive ? 'Yes' : 'No'}
          </span>
        </div>
        <div>
          <strong>Has Default Flow:</strong>{' '}
          <span
            className={
              hasDefaultFlow === true
                ? 'text-green-600'
                : hasDefaultFlow === false
                  ? 'text-red-600'
                  : 'text-yellow-600'
            }
          >
            {hasDefaultFlow === null
              ? 'Unknown'
              : hasDefaultFlow
                ? 'Yes'
                : 'No'}
          </span>
        </div>
        <div>
          <strong>Flow Session:</strong>{' '}
          <span className={flowSession ? 'text-green-600' : 'text-red-600'}>
            {flowSession ? `ID: ${flowSession.flowId}` : 'None'}
          </span>
        </div>
        <div>
          <strong>Flow Error:</strong>{' '}
          <span className={flowError ? 'text-red-600' : 'text-green-600'}>
            {flowError || 'None'}
          </span>
        </div>
        <div>
          <strong>Is Handed Over:</strong>{' '}
          <span className={isHandedOver ? 'text-green-600' : 'text-gray-600'}>
            {isHandedOver ? 'Yes' : 'No'}
          </span>
        </div>
        <div>
          <strong>Assigned Bot ID:</strong>{' '}
          <span className={assignedBotId ? 'text-green-600' : 'text-gray-600'}>
            {assignedBotId || 'None'}
          </span>
        </div>
        <div>
          <strong>Active Flow Bot ID:</strong>{' '}
          <span
            className={activeFlowBotId ? 'text-green-600' : 'text-gray-600'}
          >
            {activeFlowBotId || 'None'}
          </span>
        </div>

        <hr className="my-2" />

        <div>
          <div className="flex justify-between items-center">
            <strong>API Test:</strong>
            <button
              onClick={testFlowAPI}
              className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600"
            >
              Test API
            </button>
          </div>
          {apiTestResult && (
            <pre className="mt-1 bg-gray-100 p-2 rounded text-xs overflow-auto max-h-32">
              {JSON.stringify(apiTestResult, null, 2)}
            </pre>
          )}
        </div>

        <div>
          <div className="flex justify-between items-center">
            <strong>Init Test:</strong>
            <div className="flex gap-1">
              <button
                onClick={testFlowInit}
                disabled={!sessionId}
                className="bg-purple-500 text-white px-2 py-1 rounded text-xs hover:bg-purple-600 disabled:bg-gray-400"
              >
                Test Init
              </button>
              <button
                onClick={testFlowNodes}
                className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
              >
                Check Nodes
              </button>
            </div>
          </div>
          {initTestResult && (
            <pre className="mt-1 bg-gray-100 p-2 rounded text-xs overflow-auto max-h-32">
              {JSON.stringify(initTestResult, null, 2)}
            </pre>
          )}
        </div>

        {initializeFlow && (
          <div>
            <div className="flex justify-between items-center">
              <strong>Force Init:</strong>
              <button
                onClick={initializeFlow}
                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
              >
                Force Init
              </button>
            </div>
            <div className="text-xs text-gray-600 mt-1">
              Manually trigger flow initialization
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default FlowDebugger
